import React, { useEffect, useState } from "react";
import { Plus, Trash2, Star, Pencil, X, Quote } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import AdminLayout from "../components/AdminLayout";

const EMPTY = { name: "", location: "", quote: "", rating: 5, featured: true, order_index: 0 };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | EMPTY | existing record
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/testimonials");
      setItems(r.data || []);
    } catch (_) {
      toast.error("Could not load testimonials.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => setEditing({ ...EMPTY });
  const startEdit = (t) => setEditing({ ...t });
  const close = () => setEditing(null);

  const save = async () => {
    if (!editing.name?.trim() || !editing.quote?.trim()) {
      toast.error("Name and quote are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        location: (editing.location || "").trim(),
        quote: editing.quote.trim(),
        rating: Math.min(5, Math.max(1, Number(editing.rating) || 5)),
        featured: !!editing.featured,
        order_index: Number(editing.order_index) || 0,
      };
      if (editing.id) {
        await api.put(`/testimonials/${editing.id}`, payload);
        toast.success("Testimonial updated.");
      } else {
        await api.post("/testimonials", payload);
        toast.success("Testimonial added.");
      }
      close();
      await load();
    } catch (_) {
      toast.error("Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await api.delete(`/testimonials/${id}`);
      toast.success("Deleted.");
      await load();
    } catch (_) {
      toast.error("Could not delete.");
    }
  };

  const toggleFeatured = async (t) => {
    try {
      await api.put(`/testimonials/${t.id}`, { featured: !t.featured });
      setItems((prev) => prev.map((x) => (x.id === t.id ? { ...x, featured: !t.featured } : x)));
    } catch (_) {
      toast.error("Could not update.");
    }
  };

  return (
    <AdminLayout
      title="Testimonials."
      action={
        <button onClick={startNew} className="btn-primary !px-5 !py-3" data-testid="admin-testimonials-new">
          <Plus size={14} strokeWidth={1.5} /> New testimonial
        </button>
      }
    >
      <div data-testid="admin-testimonials-page">
        {loading ? (
          <div className="py-20 text-center text-[var(--mn-ink-2)]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-[var(--mn-border-strong)] py-20 text-center">
            <Quote size={32} strokeWidth={1.2} className="mx-auto text-[var(--mn-ink-2)]" />
            <p className="font-display text-2xl mt-4">No testimonials yet.</p>
            <p className="text-sm text-[var(--mn-ink-2)] mt-2">Add your first one to surface social proof on the homepage.</p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admin-testimonials-list">
            {items.map((t) => (
              <li
                key={t.id}
                className="border border-[var(--mn-border-strong)] p-6 bg-white flex flex-col"
                data-testid={`admin-testimonial-${t.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{t.name}</p>
                    {t.location && (
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-ink-2)] mt-1">
                        {t.location}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={12}
                        strokeWidth={1.4}
                        className={i < t.rating ? "text-[var(--mn-ink)] fill-[var(--mn-ink)]" : "text-[var(--mn-border-strong)]"}
                      />
                    ))}
                  </div>
                </div>
                <p className="font-display text-lg mt-4 leading-snug flex-1">{`\u201C${t.quote}\u201D`}</p>
                <div className="mt-6 pt-4 border-t border-[var(--mn-border)] flex items-center justify-between gap-3 flex-wrap">
                  <button
                    onClick={() => toggleFeatured(t)}
                    className={`text-[10px] uppercase tracking-[0.22em] px-2.5 py-1.5 border ${
                      t.featured
                        ? "bg-[var(--mn-ink)] text-[var(--mn-bg)] border-[var(--mn-ink)]"
                        : "border-[var(--mn-border-strong)] text-[var(--mn-ink-2)]"
                    }`}
                    data-testid={`testimonial-featured-${t.id}`}
                  >
                    {t.featured ? "Featured" : "Hidden"}
                  </button>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-ink-2)]">
                      Order #{t.order_index}
                    </span>
                    <button
                      onClick={() => startEdit(t)}
                      className="text-[var(--mn-ink-2)] hover:text-[var(--mn-ink)]"
                      aria-label="Edit"
                      data-testid={`testimonial-edit-${t.id}`}
                    >
                      <Pencil size={14} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => remove(t.id)}
                      className="text-[var(--mn-ink-2)] hover:text-[var(--mn-accent)]"
                      aria-label="Delete"
                      data-testid={`testimonial-delete-${t.id}`}
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Modal */}
      {editing && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4"
          onClick={(e) => e.target === e.currentTarget && close()}
          data-testid="testimonial-modal"
        >
          <div className="bg-white max-w-xl w-full p-8 md:p-10 border border-[var(--mn-ink)]">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-3xl">{editing.id ? "Edit testimonial" : "New testimonial"}</h3>
              <button onClick={close} aria-label="Close"><X size={20} strokeWidth={1.5} /></button>
            </div>
            <div className="space-y-4">
              <Field label="Customer name *" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="t-name" />
              <Field label="Location (city, state)" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} testid="t-location" />
              <label className="block">
                <span className="eyebrow block mb-2">Quote *</span>
                <textarea
                  rows={4}
                  className="field-input"
                  value={editing.quote}
                  onChange={(e) => setEditing({ ...editing, quote: e.target.value })}
                  data-testid="t-quote"
                />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="eyebrow block mb-2">Rating</span>
                  <select
                    className="field-input"
                    value={editing.rating}
                    onChange={(e) => setEditing({ ...editing, rating: Number(e.target.value) })}
                    data-testid="t-rating"
                  >
                    {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n === 1 ? "" : "s"}</option>)}
                  </select>
                </label>
                <Field
                  label="Order (lower shows first)"
                  type="number"
                  value={editing.order_index}
                  onChange={(v) => setEditing({ ...editing, order_index: v })}
                  testid="t-order"
                />
              </div>
              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!editing.featured}
                  onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
                  className="w-4 h-4 accent-[var(--mn-ink)]"
                  data-testid="t-featured"
                />
                Show on homepage
              </label>
            </div>
            <div className="mt-8 flex gap-3 justify-end">
              <button onClick={close} className="btn-ghost" data-testid="t-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="t-save">
                {saving ? "Saving…" : editing.id ? "Update" : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <input
        type={type}
        className="field-input"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </label>
  );
}
